import {classNames} from '@cohort/shared-frontend/utils/classNames';
import useClickTracker from '@cohort/wallet/hooks/clickTracker';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {TrackingConfig} from '@cohort/wallet/lib/Tracking';
import type {SectionName} from '@cohort/wallet/lib/Utils';
import Color from 'color';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

type MobileNavTabsProps = {
  visibleSection: SectionName | undefined;
  sectionsNames: SectionName[];
  tracking: TrackingConfig;
};

type MobileNavButtonProps = {
  sectionName: SectionName;
  visibleSection: SectionName | undefined;
  handleClick: (anchor: string) => void;
  tracking: TrackingConfig;
};

const MobileNavButton: React.FC<MobileNavButtonProps> = ({
  sectionName,
  visibleSection,
  handleClick,
  tracking,
}) => {
  const {accentColor, hasDarkBg} = useThemeContext();
  const {t} = useTranslation('components', {keyPrefix: 'mobileNavTabs'});
  const trackingCallback = useClickTracker(
    `${tracking.namespace}.${sectionName}`,
    tracking.metadata
  );

  // i18nOwl-ignore [descriptionSectionTitle, faqSectionTitle, perksSectionTitle]
  return (
    <button
      key={sectionName}
      className={classNames(
        'my-auto -mb-px whitespace-nowrap border-b-2 py-4 text-sm',
        visibleSection === sectionName
          ? `font-bold ${hasDarkBg ? 'text-white' : 'text-black/90'}`
          : `border-transparent font-medium ${hasDarkBg ? 'text-white/50' : 'text-black/60'}`
      )}
      style={
        visibleSection === sectionName ? {borderColor: accentColor} : {borderColor: 'transparent'}
      }
      type="button"
      onClick={() => {
        trackingCallback();
        handleClick(`#${sectionName}`);
      }}
    >
      {t(`${sectionName}SectionTitle`)}
    </button>
  );
};

export const MobileNavTabs: React.FC<MobileNavTabsProps> = ({
  visibleSection,
  sectionsNames,
  tracking,
}) => {
  const {backgroundColor, hasDarkBg} = useThemeContext();
  const navigate = useNavigate();

  const handleClick = useCallback(
    (anchor: string) => {
      navigate(anchor, {replace: true});
    },
    [navigate]
  );

  return (
    <div
      className={classNames(
        'z-[2] flex h-14 space-x-5 border-b px-5 backdrop-blur-sm',
        hasDarkBg ? 'border-white/20' : 'border-black/20'
      )}
      style={{backgroundColor: Color(backgroundColor).alpha(0.7).string()}}
    >
      {sectionsNames.map(sectionName => (
        <MobileNavButton
          key={`tab-${sectionName}`}
          sectionName={sectionName}
          visibleSection={visibleSection}
          handleClick={handleClick}
          tracking={tracking}
        />
      ))}
    </div>
  );
};
